/**
 * Slide 5 — Resumo Executivo / Conclusões
 * VERSÃO MINIMALISTA EXECUTIVA | Premium Board-Ready
 * 
 * Estrutura:
 * 1. Título + Subtítulo editorial
 * 2. 3 Conclusões principais (grandes cartões)
 * 3. Faixa de prioridades de gestão
 * 4. Footer
 * 
 * Canvas: 1600 x 900 px
 */

import React, { useState, useEffect } from 'react';

// ============ DESIGN TOKENS ============
const COLORS = {
  pageBg: '#0A192F',           // Navy background
  contentBg: '#F7F6F2',        // Creme panel
  gold: '#C8A13A',
  textPrimary: '#0A192F',
  textSecondary: '#465467',
  textMuted: '#7A8594',
  textWhite: '#FFFFFF',
  positive: '#16A34A',
  negative: '#C41E6D',
  warning: '#F59E0B',
};

const FONTS = {
  serif: '"Merriweather", Georgia, "Times New Roman", serif',
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

// ============ CSS KEYFRAMES ============
const injectAnimationStyles = () => {
  const styleId = 'slide5-animations';
  if (document.getElementById(styleId)) return;
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes slide5FadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slide5FadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slide5ScaleIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes slide5PulseGlow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(200, 161, 58, 0); }
      50% { box-shadow: 0 0 12px 2px rgba(200, 161, 58, 0.3); }
    }
  `;
  document.head.appendChild(style);
};

// ============ CONCLUSION CARD (Large) ============
const ConclusionCard = ({ number, icon, title, description, highlight, isAnimated, delay }) => (
  <div style={{
    flex: 1,
    backgroundColor: COLORS.contentBg,
    borderRadius: 12,
    padding: '24px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    opacity: isAnimated ? 1 : 0,
    animation: isAnimated ? `slide5ScaleIn 0.5s ease-out ${delay}s both` : 'none',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'default',
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.15)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
  }}
  >
    {/* Header with number and icon */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        backgroundColor: COLORS.pageBg,
        color: COLORS.textWhite,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: FONTS.sans,
        fontSize: 16,
        fontWeight: 700,
      }}>
        {number}
      </div>
      <span style={{ fontSize: 24 }}>{icon}</span>
    </div>
    
    {/* Title */}
    <h3 style={{
      fontFamily: FONTS.serif,
      fontSize: 18,
      fontWeight: 700,
      color: COLORS.textPrimary,
      margin: 0,
      lineHeight: 1.3,
    }}>
      {title}
    </h3>
    
    {/* Description */}
    <p style={{
      fontFamily: FONTS.sans,
      fontSize: 13,
      fontWeight: 400,
      color: COLORS.textSecondary,
      margin: 0,
      lineHeight: 1.6,
      flex: 1,
    }}>
      {description}
    </p>
    
    {/* Highlight metric */}
    {highlight && (
      <div style={{
        backgroundColor: 'rgba(200, 161, 58, 0.1)',
        borderLeft: `3px solid ${COLORS.gold}`,
        padding: '10px 14px',
        borderRadius: '0 6px 6px 0',
      }}>
        <span style={{
          fontFamily: FONTS.sans,
          fontSize: 14,
          fontWeight: 700,
          color: COLORS.gold,
        }}>
          {highlight}
        </span>
      </div>
    )}
  </div>
);

// ============ PRIORITY ITEM ============
const PriorityItem = ({ number, title, isAnimated, delay }) => (
  <div style={{
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '16px 20px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: 8,
    borderLeft: `3px solid ${COLORS.gold}`,
    opacity: isAnimated ? 1 : 0,
    animation: isAnimated ? `slide5FadeInUp 0.4s ease-out ${delay}s both` : 'none',
  }}>
    <div style={{
      width: 28,
      height: 28,
      borderRadius: '50%',
      backgroundColor: COLORS.gold,
      color: COLORS.pageBg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONTS.sans,
      fontSize: 13,
      fontWeight: 700,
      flexShrink: 0,
    }}>
      {number}
    </div>
    <span style={{
      fontFamily: FONTS.sans,
      fontSize: 13,
      fontWeight: 600,
      color: COLORS.textWhite,
      lineHeight: 1.4,
    }}>
      {title}
    </span>
  </div>
);

// ============ MAIN SLIDE ============
const IncomeStatementSlide5 = ({ data, language = 'pt' }) => {
  const [isAnimated, setIsAnimated] = useState(false);
  
  useEffect(() => {
    injectAnimationStyles();
    const timer = setTimeout(() => setIsAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Default data - can be overridden via props
  const d = data || {
    ebitdaGrowth: '+49%',
    marginExpansion: '+3.8 pp',
    cashConversion: '84%',
  };

  // Conclusions content
  const conclusions = language === 'pt' ? [
    {
      icon: '📈',
      title: 'Crescimento Sustentado',
      description: 'O EBITDA do 1T26 confirma a trajectória de crescimento, suportado por volume acima do orçamento e ganhos de eficiência operacional que compensam a pressão de preço.',
      highlight: `EBITDA ${d.ebitdaGrowth} vs 1T25`,
    },
    {
      icon: '📊',
      title: 'Margem em Expansão',
      description: 'A margem EBITDA expande graças ao efeito de escala e disciplina de custos, demonstrando a capacidade de diluir custos fixos com o aumento de volume.',
      highlight: `Margem ${d.marginExpansion}`,
    },
    {
      icon: '💰',
      title: 'Conversão em Caixa Robusta',
      description: 'A conversão de EBITDA em caixa operacional mantém-se elevada, preservando a capacidade de investimento e o conforto do balanço.',
      highlight: `Conversão FCO ${d.cashConversion}`,
    },
  ] : [
    {
      icon: '📈',
      title: 'Sustained Growth',
      description: 'Q1 26 EBITDA confirms the growth trajectory, supported by volume above budget and operational efficiency gains that offset price pressure.',
      highlight: `EBITDA ${d.ebitdaGrowth} vs Q1 25`,
    },
    {
      icon: '📊',
      title: 'Margin Expansion',
      description: 'EBITDA margin expands thanks to scale effect and cost discipline, demonstrating the ability to dilute fixed costs with volume increase.',
      highlight: `Margin ${d.marginExpansion}`,
    },
    {
      icon: '💰',
      title: 'Robust Cash Conversion',
      description: 'EBITDA to operating cash conversion remains high, preserving investment capacity and balance sheet comfort.',
      highlight: `OCF Conversion ${d.cashConversion}`,
    },
  ];

  // Management priorities
  const priorities = language === 'pt' ? [
    'Proteger preço médio e optimizar mix comercial',
    'Manter disciplina de custos e capturar escala',
    'Assegurar execução operacional e conversão em caixa',
  ] : [
    'Protect average price and optimize commercial mix',
    'Maintain cost discipline and capture scale',
    'Ensure operational execution and cash conversion',
  ];

  // Closing statement
  const closingStatement = language === 'pt'
    ? 'O trimestre valida a estratégia de crescimento com rentabilidade. A gestão deve agora assegurar a sustentabilidade desta trajectória.'
    : 'The quarter validates the growth with profitability strategy. Management must now ensure the sustainability of this trajectory.';

  return (
    <div style={{ 
      width: 1600, 
      height: 900, 
      backgroundColor: COLORS.pageBg, 
      position: 'relative', 
      fontFamily: FONTS.sans, 
      overflow: 'hidden' 
    }}>
      
      {/* ============ BLOCO 1: TÍTULO + SUBTÍTULO ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        top: 48, 
        right: 60,
        opacity: isAnimated ? 1 : 0,
        animation: isAnimated ? 'slide5FadeIn 0.6s ease-out 0s both' : 'none',
      }}>
        <h1 style={{ 
          fontFamily: FONTS.serif, 
          fontSize: 34, 
          lineHeight: '42px', 
          fontWeight: 700, 
          color: COLORS.textWhite, 
          margin: 0,
        }}>
          {language === 'pt' ? 'Resumo Executivo' : 'Executive Summary'}
        </h1>
        
        <p style={{ 
          fontFamily: FONTS.sans, 
          fontSize: 15, 
          color: '#64748B', 
          marginTop: 12,
          marginBottom: 0,
        }}>
          {language === 'pt'
            ? 'Síntese das principais conclusões e prioridades de gestão para o trimestre'
            : 'Summary of key conclusions and management priorities for the quarter'}
        </p>
      </div>

      {/* ============ BLOCO 2: 3 CONCLUSÕES PRINCIPAIS ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        top: 150, 
        right: 60,
        display: 'flex',
        gap: 24,
      }}>
        {conclusions.map((conclusion, i) => (
          <ConclusionCard
            key={i}
            number={i + 1}
            icon={conclusion.icon}
            title={conclusion.title}
            description={conclusion.description}
            highlight={conclusion.highlight}
            isAnimated={isAnimated}
            delay={0.2 + i * 0.15}
          />
        ))}
      </div>

      {/* ============ BLOCO 3: FAIXA DE PRIORIDADES ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        top: 520, 
        right: 60,
        opacity: isAnimated ? 1 : 0,
        animation: isAnimated ? 'slide5FadeIn 0.5s ease-out 0.7s both' : 'none',
      }}>
        {/* Section title */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 20,
        }}>
          <div style={{
            width: 4,
            height: 24,
            backgroundColor: COLORS.gold,
            borderRadius: 2,
          }} />
          <h2 style={{
            fontFamily: FONTS.serif,
            fontSize: 18,
            fontWeight: 700,
            color: COLORS.textWhite,
            margin: 0,
          }}>
            {language === 'pt' ? 'Prioridades de Gestão' : 'Management Priorities'}
          </h2>
        </div>
        
        {/* Priority cards */}
        <div style={{ display: 'flex', gap: 20 }}>
          {priorities.map((priority, i) => (
            <PriorityItem
              key={i}
              number={i + 1}
              title={priority}
              isAnimated={isAnimated}
              delay={0.9 + i * 0.1}
            />
          ))}
        </div>
      </div>

      {/* ============ BLOCO 4: FRASE DE FECHO ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        right: 60, 
        bottom: 80,
        opacity: isAnimated ? 1 : 0,
        animation: isAnimated ? 'slide5FadeInUp 0.5s ease-out 1.2s both' : 'none',
      }}>
        <div style={{
          backgroundColor: 'rgba(200, 161, 58, 0.08)',
          borderRadius: 12,
          padding: '20px 28px',
          borderLeft: `4px solid ${COLORS.gold}`,
        }}>
          <p style={{
            fontFamily: FONTS.serif,
            fontSize: 16,
            fontStyle: 'italic',
            color: COLORS.textWhite,
            margin: 0,
            lineHeight: 1.6,
          }}>
            "{closingStatement}"
          </p>
        </div>
      </div>

      {/* ============ FOOTER ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        right: 60, 
        bottom: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        opacity: isAnimated ? 1 : 0,
        animation: isAnimated ? 'slide5FadeIn 0.4s ease-out 1.4s both' : 'none',
      }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#64748B' }}>
          {language === 'pt' 
            ? 'ABC | Demonstração de Resultados Executiva | Versão final de apresentação'
            : 'ABC | Executive Income Statement | Final presentation version'}
        </span>
        <span style={{ 
          fontFamily: FONTS.sans, 
          fontSize: 10, 
          color: COLORS.gold, 
          fontWeight: 600,
          animation: isAnimated ? 'slide5PulseGlow 2s ease-in-out 2s infinite' : 'none',
          padding: '4px 12px',
          borderRadius: 4,
        }}>
          Board-ready
        </span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#64748B' }}>5</span>
      </div>
    </div>
  );
};

export default IncomeStatementSlide5;
